// Copyright (c) 2025 Cozy Intellect Inc.
// File Path: packages/chatlib/src/utils/taskPlanAsset.ts

import type { AssetData, AssetFull } from '@repo/common/types/Asset';
import type { Action, TaskPlan } from '@repo/common/types/TaskSynthesis';

/**
 * TaskPlanAssetData extends AssetData with task-specific fields
 * for storing TaskPlan information in the Asset system.
 */
export interface TaskPlanAssetData extends AssetData {
  taskId: string;
  actions: Action[];
  metadata: {
    modelUsed: string;
    generationTimeMs: number;
    promptTokens: number;
    completionTokens: number;
  };
  generatedAt: string;
  sourceTaskId?: string; // Original task ID from meeting notes
  sourceTranscriptId?: string; // Meeting transcript ID
}


/**
 * Convert TaskPlan to AssetFull for persistence.
 * 
 * Stores:
 * - Header fields (context, importance, costOfInaction) in content as JSON
 * - Title in title field
 * - Context summary in summary field
 * - Actions and metadata in data field
 * 
 * @param taskPlan - The TaskPlan to convert
 * @param userId - The user ID who owns this asset
 * @param workspaceId - Optional workspace ID
 * @returns AssetFull object ready for persistence
 */
export function taskPlanToAsset(
  taskPlan: TaskPlan,
  userId: string,
  workspaceId?: string
): AssetFull {
  return {
    _type: 'assetFull',
    id: taskPlan.taskId, // Use taskId as asset ID
    assetType: 'taskplan',
    title: taskPlan.header.title,
    summary: taskPlan.header.context,
    content: JSON.stringify({
      context: taskPlan.header.context,
      importance: taskPlan.header.importance,
      costOfInaction: taskPlan.header.costOfInaction
    }),
    contentHistory: null, // TaskPlans don't use Lexical editor state
    data: {
      taskId: taskPlan.taskId,
      actions: taskPlan.actions,
      metadata: taskPlan.metadata,
      generatedAt: taskPlan.generatedAt
    } as TaskPlanAssetData,
    attachments: null,
    userId,
    workspaceId,
    createdAt: new Date(),
    updatedAt: new Date()
  };
}


/**
 * Convert AssetFull back to TaskPlan.
 * 
 * Reconstructs:
 * - Header from content JSON and title/summary fields
 * - Actions from data.actions
 * - Metadata from data.metadata
 * 
 * @param asset - The AssetFull to convert
 * @returns TaskPlan object
 */
export function assetToTaskPlan(asset: AssetFull): TaskPlan {
  const data = asset.data as TaskPlanAssetData;
  const headerContent = asset.content ? JSON.parse(asset.content) : {};

  return {
    taskId: data.taskId || asset.id,
    generatedAt: data.generatedAt || asset.createdAt.toISOString(),
    header: {
      title: asset.title,
      context: headerContent.context || asset.summary || '',
      importance: headerContent.importance || '',
      costOfInaction: headerContent.costOfInaction || null
    },
    actions: data.actions || [],
    metadata: data.metadata || {
      modelUsed: '',
      generationTimeMs: 0,
      promptTokens: 0,
      completionTokens: 0
    }
  };
}
